import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import {
  ArrowRight, Menu, X, CheckCircle2, Check, MessageCircle, ReceiptIndianRupee,
  Warehouse, Smartphone, Store, BarChart3, Inbox, Clock3, Boxes, BellOff, Navigation,
  Plug, Wrench, Droplets, Factory, ShieldCheck, Loader2, Phone as PhoneIcon,
  Car, Sprout, Pill,
} from "lucide-react";

const SALES_EMAIL = "Sales@tradedesk360.com";
const HELLO_EMAIL = "hello@tradedesk360.com";

const IMG = {
  hero: "/land-hero.jpg",
  electrical: "/land-electrical.jpg",
  hardware: "/land-hardware.jpg",
  building: "/land-building.jpg",
  factory: "/land-factory.jpg",
  retail: "https://images.pexels.com/photos/15491784/pexels-photo-15491784.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  auto: "https://images.unsplash.com/photo-1702146715471-ae6b10689969?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  agri: "https://images.pexels.com/photos/33189479/pexels-photo-33189479.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  pharma: "https://images.unsplash.com/photo-1642055514517-7b52288890ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
};

/* ---------------- Header ---------------- */
const NAV = [
  { label: "Pain Points", href: "#pain-points" },
  { label: "Solutions", href: "#solutions" },
  { label: "Who It's For", href: "#who-its-for" },
  { label: "Pricing", href: "#pricing" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${solid ? "bg-[#101D51]/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"}`} data-testid="landing-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2" data-testid="landing-logo">
          <img src="/tradedesk-mark.png" alt="TradeDesk360" className="w-9 h-9 rounded-lg bg-white p-1 object-contain" />
          <span className="ff-head font-extrabold text-white text-lg tracking-tight">TradeDesk360</span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200" data-testid={`nav-${n.href.slice(1)}`}>{n.label}</a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <AppLink to="/login" className="text-sm font-semibold text-white/80 hover:text-white transition-colors duration-200" data-testid="nav-signin-link">Sign in</AppLink>
          <AppLink to="/signup" data-testid="nav-start-trial-button">
            <Button className="h-10 px-5 rounded-full bg-[#F83A84] hover:bg-[#E02871] text-white font-semibold transition-colors duration-200">Start free trial</Button>
          </AppLink>
        </div>
        <button className="md:hidden text-white p-2 -mr-2" onClick={() => setOpen(!open)} data-testid="landing-mobile-menu-btn">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-[#101D51] border-t border-white/10 px-4 py-4 space-y-3" data-testid="landing-mobile-menu">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block text-sm font-medium text-white/80 py-1">{n.label}</a>
          ))}
          <div className="flex gap-3 pt-2">
            <AppLink to="/login" className="flex-1" data-testid="nav-signin-link-mobile"><Button variant="outline" className="w-full h-10 rounded-full border-white/30 text-white bg-transparent hover:bg-white/10">Sign in</Button></AppLink>
            <AppLink to="/signup" className="flex-1" data-testid="nav-start-trial-button-mobile"><Button className="w-full h-10 rounded-full bg-[#F83A84] hover:bg-[#E02871] text-white font-semibold">Free trial</Button></AppLink>
          </div>
        </div>
      )}
    </header>
  );
}

/* ---------------- Hero ---------------- */
function Hero() {
  return (
    <section id="top" className="relative bg-[#101D51] text-white overflow-hidden">
      <div className="absolute -right-24 -top-24 w-[28rem] h-[28rem] rounded-full bg-[#F83A84]/20 blur-3xl" />
      <div className="absolute -left-20 bottom-0 w-96 h-96 rounded-full bg-[#0B7C5A]/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 sm:pt-36 sm:pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-semibold text-white/90">
              <Factory className="w-3.5 h-3.5" /> Built for Distributors, Manufacturers & B2B Traders
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 px-3 py-1 text-xs font-semibold text-[#8ff0b8]">
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp-native
            </span>
          </div>
          <h1 className="ff-head text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Turn WhatsApp enquiries into <span className="text-[#F83A84]">quotes, orders & GST invoices</span>.
          </h1>
          <p className="ff-body text-base sm:text-lg text-white/70 mt-5 max-w-xl leading-relaxed">
            The all-in-one sales & operations platform for Indian B2B businesses — enquiry-to-invoice pipelines, multi-bin inventory, vendor purchase orders, field sales and analytics, all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <AppLink to="/signup" data-testid="hero-start-trial-button">
              <Button className="w-full sm:w-auto h-12 px-7 rounded-full bg-[#F83A84] hover:bg-[#E02871] text-white font-semibold text-base transition-colors duration-200">
                Start free trial <ArrowRight className="w-4 h-4" />
              </Button>
            </AppLink>
            <a href="#demo" data-testid="hero-request-demo-button">
              <Button variant="outline" className="w-full sm:w-auto h-12 px-7 rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 font-semibold text-base transition-colors duration-200">
                Request a demo
              </Button>
            </a>
          </div>
          <p className="ff-body text-xs text-white/50 mt-5 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#0B7C5A]" /> 15-day free trial · No credit card · 2-minute setup
          </p>
        </div>
        <div className="relative">
          <FlowMock />
        </div>
      </div>
    </section>
  );
}

function FlowMock() {
  return (
    <div className="relative mx-auto max-w-md">
      <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
        <img src={IMG.hero} alt="Distribution warehouse manager using TradeDesk360" className="w-full h-48 object-cover" />
      </div>
      <div className="absolute -bottom-6 -left-2 sm:-left-8 w-60 bg-white rounded-2xl shadow-xl p-3 animate-float">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full bg-[#25D366] flex items-center justify-center"><MessageCircle className="w-4 h-4 text-white" /></div>
          <span className="ff-head text-xs font-bold text-[#101D51]">WhatsApp enquiry</span>
        </div>
        <div className="bg-[#DCF8C6] rounded-lg rounded-tl-none px-3 py-2 text-[11px] text-slate-700">"Need 200m 4sq wire + 50 MCBs. Rate & delivery?"</div>
      </div>
      <div className="absolute -top-5 -right-2 sm:-right-6 w-52 bg-white rounded-2xl shadow-xl p-3 animate-float" style={{ animationDelay: "1.2s" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="ff-head text-xs font-bold text-[#101D51]">GST Invoice</span>
          <span className="text-[9px] font-bold text-[#0B7C5A] bg-[#E6F5F0] px-1.5 py-0.5 rounded">PAID</span>
        </div>
        <div className="space-y-1.5">
          <div className="h-1.5 bg-slate-100 rounded w-full" />
          <div className="h-1.5 bg-slate-100 rounded w-4/5" />
          <div className="flex justify-between items-center pt-1">
            <span className="text-[10px] text-slate-400">Total</span>
            <span className="ff-head text-sm font-extrabold text-[#101D51]">₹48,320</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Pain Points ---------------- */
const PAINS = [
  { icon: Inbox, title: "Enquiries lost in WhatsApp", body: "High-value buyer messages get buried across personal chats and staff phones — no tracking, no ownership, no follow-up." },
  { icon: Clock3, title: "Slow, manual quotes & invoices", body: "Building rate sheets and GST invoices by hand costs hours a day and delays deals your competitors close faster." },
  { icon: Boxes, title: "Stock chaos across godowns", body: "Mismatches between shop floor, warehouse racks and bin locations lead to overselling and dead stock." },
  { icon: BellOff, title: "Receivables & follow-ups slip", body: "Forgotten payment reminders and quote follow-ups quietly bleed revenue every single month." },
  { icon: Navigation, title: "Blind field sales", body: "Reps on the road can't see live stock or prices, and can't raise orders or POs on the spot." },
];

function PainPoints() {
  return (
    <section id="pain-points" className="scroll-mt-20 bg-[#F8F9FC] py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="ff-head text-xs font-bold uppercase tracking-widest text-[#F83A84]">The daily grind</p>
        <h2 className="ff-head text-2xl sm:text-4xl font-bold tracking-tight text-[#101D51] mt-3 max-w-2xl">Losing orders to WhatsApp mess and paper registers?</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {PAINS.map((p, i) => (
            <div key={p.title} className={`bg-white rounded-2xl border border-[#E6E8F0] p-6 hover:shadow-md transition-shadow duration-200 ${i === 0 ? "lg:col-span-1" : ""}`} data-testid={`pain-card-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#F83A84]/10 flex items-center justify-center mb-4">
                <p.icon className="w-5 h-5 text-[#F83A84]" />
              </div>
              <h3 className="ff-head text-lg font-semibold text-[#101D51]">{p.title}</h3>
              <p className="ff-body text-sm text-[#6B7189] mt-2 leading-relaxed">{p.body}</p>
            </div>
          ))}
          <div className="rounded-2xl bg-[#101D51] p-6 flex flex-col justify-center" data-testid="pain-card-cta">
            <p className="ff-head text-lg font-bold text-white">One platform fixes all of this.</p>
            <p className="ff-body text-sm text-white/60 mt-2">See how TradeDesk360 replaces the chaos with a clean enquiry-to-invoice pipeline.</p>
            <a href="#solutions" className="ff-head text-sm font-semibold text-[#F83A84] mt-4 inline-flex items-center gap-1">Explore solutions <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Solutions ---------------- */
const SOLUTIONS = [
  { icon: MessageCircle, title: "WhatsApp inbox → Enquiry desk", body: "Every chat becomes a tracked enquiry with owner, status and SLA — reply and quote from inside the app.", tone: "green" },
  { icon: ReceiptIndianRupee, title: "1-tap quotes, orders & GST invoices", body: "Convert an enquiry to a compliant GST invoice in seconds and send the PDF straight back to WhatsApp.", tone: "pink" },
  { icon: Warehouse, title: "Multi-bin inventory & stock", body: "Track stock by warehouse, rack and bin with reserved vs available rollups across locations.", tone: "navy" },
  { icon: BarChart3, title: "Vendor POs & analytics", body: "Raise purchase orders to suppliers and see live sales, activity and conversion dashboards with drill-down.", tone: "navy" },
  { icon: Smartphone, title: "Field sales app", body: "Reps book orders, check live prices and log visits on the road — online or offline.", tone: "pink" },
  { icon: Store, title: "Retailer ordering portal", body: "Give your regular B2B buyers a self-serve 24/7 portal to reorder from your catalog.", tone: "green" },
];
const TONE = {
  green: "bg-[#E6F5F0] text-[#0B7C5A]",
  pink: "bg-[#F83A84]/10 text-[#F83A84]",
  navy: "bg-[#101D51]/10 text-[#101D51]",
};

function Solutions() {
  return (
    <section id="solutions" className="scroll-mt-20 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="ff-head text-xs font-bold uppercase tracking-widest text-[#0B7C5A]">What we solve</p>
          <h2 className="ff-head text-2xl sm:text-4xl font-bold tracking-tight text-[#101D51] mt-3">From WhatsApp chaos to enterprise control — in one tap.</h2>
          <p className="ff-body text-base sm:text-lg text-[#6B7189] mt-4">Everything your sales and operations teams need, replacing five disconnected tools and endless spreadsheets.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {SOLUTIONS.map((s, i) => (
            <div key={s.title} className="bg-white rounded-2xl border border-[#E6E8F0] p-6 sm:p-7 hover:shadow-md hover:-translate-y-0.5 transition-transform duration-200" data-testid={`solution-card-${i}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${TONE[s.tone]}`}>
                <s.icon className="w-6 h-6" />
              </div>
              <h3 className="ff-head text-xl font-semibold text-[#101D51]">{s.title}</h3>
              <p className="ff-body text-sm text-[#6B7189] mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Who it's for ---------------- */
const PERSONAS = [
  { icon: Store, img: IMG.retail, title: "Retailers & Shops", body: "Run a shop or showroom? Track stock, bill fast with GST and reorder in a tap — your whole counter on one screen." },
  { icon: Plug, img: IMG.electrical, title: "Electrical & Electronics Distributors", body: "Wire coils, switchgear and appliances with batch / serial tracking and fast bulk quoting." },
  { icon: Wrench, img: IMG.hardware, title: "Hardware & Fasteners Wholesalers", body: "SKU-heavy catalogs, multi-bin godowns and slab-based bulk discounting made simple." },
  { icon: Droplets, img: IMG.building, title: "Building Material & Plumbing Suppliers", body: "Pipes, tiles and cement in lots — with field dispatch and delivery management." },
  { icon: Factory, img: IMG.factory, title: "Manufacturers & FMCG Distributors", body: "Multi-salesman routes, production stock and a retailer self-service ordering portal." },
  { icon: Car, img: IMG.auto, title: "Auto Parts & Spares Dealers", body: "Huge part-number catalogs with quick lookup, and counter-to-invoice billing in seconds." },
  { icon: Sprout, img: IMG.agri, title: "Agri Inputs & Nurseries", body: "Seeds, fertilisers and tools with season-wise stock, credit tracking and repeat ordering." },
  { icon: Pill, img: IMG.pharma, title: "Pharma & Medical Distributors", body: "Batch & expiry-aware inventory, fast order booking and reliable retailer supply." },
];

function WhoItsFor() {
  return (
    <section id="who-its-for" className="scroll-mt-20 bg-[#101D51] py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="ff-head text-xs font-bold uppercase tracking-widest text-[#F83A84]">Who it's built for</p>
        <h2 className="ff-head text-2xl sm:text-4xl font-bold tracking-tight text-white mt-3 max-w-2xl">A 360° platform for every trade business.</h2>
        <p className="ff-body text-base text-white/60 mt-4 max-w-2xl">From a single-branch retailer managing stock to a multi-warehouse distributor serving hundreds of buyers — TradeDesk360 fits the way you already work, from the first WhatsApp message to the paid invoice.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {PERSONAS.map((p, i) => (
            <div key={p.title} className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/25 transition-colors duration-200" data-testid={`persona-card-${i}`}>
              <div className="h-36 overflow-hidden">
                <img src={p.img} alt={p.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <div className="w-10 h-10 rounded-lg bg-[#F83A84]/15 flex items-center justify-center mb-3"><p.icon className="w-5 h-5 text-[#F83A84]" /></div>
                <h3 className="ff-head text-base font-semibold text-white leading-snug">{p.title}</h3>
                <p className="ff-body text-xs text-white/60 mt-2 leading-relaxed">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Pricing ---------------- */
const PLANS = [
  { name: "Starter Trade", tag: "For growing traders", features: ["WhatsApp inbox & enquiry desk", "Quotes, orders & GST invoices", "Single warehouse inventory", "Up to 3 users"], popular: false },
  { name: "Business Growth", tag: "Most popular", features: ["Everything in Starter", "Multi-bin inventory & vendor POs", "Field sales mobile app", "Analytics & dashboards", "Up to 15 users"], popular: true },
  { name: "Enterprise Multi-Bin", tag: "For distributors at scale", features: ["Everything in Growth", "Retailer ordering portal", "Custom GST invoice templates", "Priority support & onboarding", "Unlimited users"], popular: false },
];

function Pricing() {
  const mailto = `mailto:${SALES_EMAIL}?subject=TradeDesk360%20Pricing%20Enquiry`;
  return (
    <section id="pricing" className="scroll-mt-20 py-16 sm:py-24 bg-[#F8F9FC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="ff-head text-xs font-bold uppercase tracking-widest text-[#0B7C5A]">Pricing</p>
          <h2 className="ff-head text-2xl sm:text-4xl font-bold tracking-tight text-[#101D51] mt-3">Plans that scale with your business.</h2>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E6F5F0] text-[#0B7C5A] px-3 py-1 text-xs font-semibold mt-4">
            <CheckCircle2 className="w-3.5 h-3.5" /> 15-day unlimited free trial · no card required
          </span>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12 items-start">
          {PLANS.map((plan, i) => (
            <div key={plan.name} className={`relative rounded-2xl p-7 transition-shadow duration-200 ${plan.popular ? "bg-[#101D51] text-white shadow-xl md:-mt-4 md:mb-4" : "bg-white border border-[#E6E8F0] hover:shadow-md"}`} data-testid={`pricing-card-${i}`}>
              {plan.popular && <span className="absolute -top-3 left-7 rounded-full bg-[#F83A84] text-white text-[11px] font-bold px-3 py-1">POPULAR</span>}
              <p className={`ff-body text-xs font-semibold uppercase tracking-wider ${plan.popular ? "text-[#F83A84]" : "text-[#6B7189]"}`}>{plan.tag}</p>
              <h3 className={`ff-head text-xl font-bold mt-1 ${plan.popular ? "text-white" : "text-[#101D51]"}`}>{plan.name}</h3>
              <div className="mt-3 mb-5">
                <span className={`ff-head text-3xl font-extrabold ${plan.popular ? "text-white" : "text-[#101D51]"}`}>Custom</span>
                <span className={`ff-body text-sm ml-1 ${plan.popular ? "text-white/50" : "text-[#6B7189]"}`}>pricing</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${plan.popular ? "text-[#0B7C5A]" : "text-[#0B7C5A]"}`} />
                    <span className={`ff-body text-sm ${plan.popular ? "text-white/85" : "text-slate-700"}`}>{f}</span>
                  </li>
                ))}
              </ul>
              <a href="#demo" data-testid={`pricing-contact-sales-${i}`}>
                <Button className={`w-full h-11 rounded-full font-semibold transition-colors duration-200 ${plan.popular ? "bg-[#F83A84] hover:bg-[#E02871] text-white" : "bg-[#101D51] hover:bg-[#0B143A] text-white"}`}>
                  Contact sales
                </Button>
              </a>
            </div>
          ))}
        </div>
        <p className="ff-body text-center text-sm text-[#6B7189] mt-8">
          Questions on plans? Write to <a href={mailto} className="text-[#F83A84] font-semibold hover:underline" data-testid="pricing-sales-email">{SALES_EMAIL}</a>
        </p>
      </div>
    </section>
  );
}

/* ---------------- Demo + Trial ---------------- */
const INDUSTRIES = ["Electrical & Electronics", "Hardware & Fasteners", "Building Material & Plumbing", "Manufacturing", "FMCG / General Trade", "Other"];

function DemoSection() {
  const [f, setF] = useState({ name: "", business: "", phone: "", email: "", industry: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.name.trim() || f.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please add your name and a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    try {
      await api.post("/public/demo-request", f);
      setDone(true);
      toast.success("Request received — our team will reach out shortly.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" className="scroll-mt-20 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-stretch">
        {/* Left: trial pitch */}
        <div className="rounded-3xl bg-[#101D51] text-white p-8 sm:p-10 flex flex-col justify-between">
          <div>
            <p className="ff-head text-xs font-bold uppercase tracking-widest text-[#F83A84]">Get started free</p>
            <h2 className="ff-head text-2xl sm:text-4xl font-bold tracking-tight mt-3">Start your 15-day free trial today.</h2>
            <p className="ff-body text-base text-white/70 mt-4">Set up your business in under two minutes. No credit card, full access to every feature.</p>
            <ul className="mt-6 space-y-3">
              {["Full access for 15 days", "GST-ready invoices & PDFs", "WhatsApp inbox, inventory & field sales", "Cancel anytime — no card required"].map((t) => (
                <li key={t} className="flex items-center gap-2.5 ff-body text-sm text-white/85"><CheckCircle2 className="w-4 h-4 text-[#0B7C5A]" /> {t}</li>
              ))}
            </ul>
          </div>
          <AppLink to="/signup" data-testid="demo-start-trial-button" className="mt-8">
            <Button className="w-full sm:w-auto h-12 px-8 rounded-full bg-[#F83A84] hover:bg-[#E02871] text-white font-semibold text-base transition-colors duration-200">
              Start free trial <ArrowRight className="w-4 h-4" />
            </Button>
          </AppLink>
        </div>

        {/* Right: demo form */}
        <div className="rounded-3xl bg-white border border-[#E6E8F0] p-8 sm:p-10 shadow-sm">
          {done ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-8" data-testid="demo-form-success">
              <div className="w-16 h-16 rounded-full bg-[#E6F5F0] flex items-center justify-center mb-5"><CheckCircle2 className="w-8 h-8 text-[#0B7C5A]" /></div>
              <h3 className="ff-head text-2xl font-bold text-[#101D51]">Thank you!</h3>
              <p className="ff-body text-sm text-[#6B7189] mt-2 max-w-xs">Your demo request is in. Our team will contact you shortly. For anything urgent, reach us at <a href={`mailto:${SALES_EMAIL}`} className="text-[#F83A84] font-semibold">{SALES_EMAIL}</a>.</p>
              <AppLink to="/signup" className="mt-6" data-testid="demo-success-trial-button"><Button variant="outline" className="h-11 px-6 rounded-full border-[#E6E8F0] font-semibold">Or start free trial now</Button></AppLink>
            </div>
          ) : (
            <>
              <h3 className="ff-head text-2xl font-bold text-[#101D51]">Request a live demo</h3>
              <p className="ff-body text-sm text-[#6B7189] mt-1 mb-6">See TradeDesk360 mapped to your exact sales & inventory workflow.</p>
              <div className="space-y-3.5">
                <div className="grid sm:grid-cols-2 gap-3.5">
                  <Field label="Full name *"><Input data-testid="demo-form-name" value={f.name} onChange={set("name")} placeholder="Your name" className="h-11" /></Field>
                  <Field label="Business name"><Input data-testid="demo-form-business" value={f.business} onChange={set("business")} placeholder="Your company" className="h-11" /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-3.5">
                  <Field label="Mobile *"><Input data-testid="demo-form-phone" value={f.phone} onChange={set("phone")} inputMode="numeric" placeholder="9876500001" className="h-11" /></Field>
                  <Field label="Email"><Input data-testid="demo-form-email" value={f.email} onChange={set("email")} type="email" placeholder="you@company.com" className="h-11" /></Field>
                </div>
                <Field label="Industry">
                  <select data-testid="demo-form-industry" value={f.industry} onChange={set("industry")} className="ff-body w-full h-11 rounded-md border border-[#E6E8F0] bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#101D51]/20">
                    <option value="">Select your industry</option>
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </Field>
                <Field label="Message">
                  <textarea data-testid="demo-form-message" value={f.message} onChange={set("message")} rows={3} placeholder="Tell us about your business & needs" className="ff-body w-full rounded-md border border-[#E6E8F0] bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#101D51]/20" />
                </Field>
                <Button onClick={submit} disabled={loading} data-testid="demo-form-submit-btn" className="w-full h-12 rounded-full bg-[#101D51] hover:bg-[#0B143A] text-white font-semibold text-base transition-colors duration-200">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Request demo <ArrowRight className="w-4 h-4" /></>}
                </Button>
                <p className="ff-body text-xs text-center text-[#6B7189]">Prefer email? <a href={`mailto:${SALES_EMAIL}`} className="text-[#F83A84] font-semibold" data-testid="demo-sales-email">{SALES_EMAIL}</a></p>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="ff-body text-xs font-medium text-[#6B7189]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/* ---------------- Footer ---------------- */
function Footer() {
  return (
    <footer className="bg-[#0B143A] text-white/70 py-12" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src="/tradedesk-mark.png" alt="TradeDesk360" className="w-9 h-9 rounded-lg bg-white p-1 object-contain" />
            <span className="ff-head font-extrabold text-white text-lg">TradeDesk360</span>
          </div>
          <p className="ff-body text-sm max-w-xs">From enquiry on WhatsApp to invoices — all managed in one platform for Indian B2B businesses.</p>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/80 mt-4"><ShieldCheck className="w-3.5 h-3.5" /> GST-ready · Made in India</span>
        </div>
        <div>
          <p className="ff-head text-sm font-bold text-white mb-3">Product</p>
          <ul className="space-y-2 ff-body text-sm">
            <li><a href="#pain-points" className="hover:text-white transition-colors duration-200">Pain points</a></li>
            <li><a href="#solutions" className="hover:text-white transition-colors duration-200">Solutions</a></li>
            <li><a href="#who-its-for" className="hover:text-white transition-colors duration-200">Who it's for</a></li>
            <li><a href="#pricing" className="hover:text-white transition-colors duration-200">Pricing</a></li>
          </ul>
        </div>
        <div>
          <p className="ff-head text-sm font-bold text-white mb-3">Get started</p>
          <ul className="space-y-2 ff-body text-sm">
            <li><AppLink to="/signup" className="hover:text-white transition-colors duration-200" data-testid="footer-trial-link">Start free trial</AppLink></li>
            <li><AppLink to="/login" className="hover:text-white transition-colors duration-200" data-testid="footer-signin-link">Sign in</AppLink></li>
            <li><a href="#demo" className="hover:text-white transition-colors duration-200">Request a demo</a></li>
            <li><a href={`mailto:${SALES_EMAIL}`} className="hover:text-white transition-colors duration-200" data-testid="footer-sales-email">{SALES_EMAIL}</a></li>
            <li className="flex items-center gap-1.5"><PhoneIcon className="w-3.5 h-3.5" /> <a href={`mailto:${HELLO_EMAIL}`} className="hover:text-white transition-colors duration-200" data-testid="footer-hello-email">{HELLO_EMAIL}</a></li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 pt-6 border-t border-white/10 ff-body text-xs text-white/40">
        © {new Date().getFullYear()} TradeDesk360. A Clotus Infotech product. All rights reserved.
      </div>
    </footer>
  );
}

const APP_URL = process.env.REACT_APP_APP_URL || "";
const AppLink = ({ to, children, ...rest }) => (
  <a href={`${APP_URL}${to}`} {...rest}>{children}</a>
);

export default function Landing() {
  return (
    <div className="ff-body min-h-full bg-white overflow-x-hidden">
      <Header />
      <Hero />
      <PainPoints />
      <Solutions />
      <WhoItsFor />
      <Pricing />
      <DemoSection />
      <Footer />
    </div>
  );
}
